// Passing Summary widget (Players canvas, `player-passing`) — old Catalyst's
// Passing "Summary" card as its own right-sized widget. Headline passing numbers
// on a POSITIONAL basis (the player's minutes in their position group), with the
// group average as context — matching old Catalyst exactly (Messi MLS 2025
// striker: 1193 passes · 79.46% vs 77.08% · 0.23 diff, "positional avg 0.22").
//
// The passing radar is its OWN widget now (`player-passing-radar`) — combining
// both in one tile made the radar overflow. Comparison group/basis/floor are
// fixed until the drawer (see PROGRESS); the group is auto-picked from the
// player's top position.

import type { WidgetProps } from "./registry.js";
import { theme } from "../lib/theme.js";
import { num, passCompletion, type StatsRow } from "./passing.js";
import { usePassingComparison } from "./usePassingComparison.js";
import PassingScope from "./PassingScope.js";

const SUMMARY_KEYS = [
  "passes",
  "passes_90",
  "pass_success_perc",
  "expected_pass_success_perc",
  "avg_pass_difficulty",
  "psax_90",
  "key_passes_90",
  "minutes",
];

export default function PassingSummaryWidget({ scope }: WidgetProps) {
  const { playerId, competitionId, seasonId } = scope;
  const cmp = usePassingComparison(playerId, competitionId, seasonId, SUMMARY_KEYS);

  if (playerId === null) return <Msg>Search for a player above.</Msg>;
  if (competitionId === null || seasonId === null)
    return <Msg>Pick a competition and season above.</Msg>;
  if (cmp.isPending) return <Msg>Loading passing…</Msg>;
  if (cmp.isError) return <Msg danger>Couldn't load passing. {String(cmp.error)}</Msg>;

  const row = cmp.playerRow;
  if (!row) return <Msg>No passing data for this player in this season.</Msg>;

  const comp = passCompletion(row);
  const avg = cmp.peerAvg; // group's league season average; null when no group

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, height: "100%" }}>
      <PassingScope cmp={cmp} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(112px, 1fr))", gap: 8 }}>
        <Stat value={fmtNum(num(row, "passes"), 0)} label="Passes" sub={`${fmtNum(num(row, "passes_90"), 1)} per 90`} />
        <Stat
          value={fmtPct(comp.actualPct)}
          label="Completion"
          sub={comp.expectedPct === null ? undefined : `xP ${fmtPct(comp.expectedPct)} · ${fmtGap(comp.gapPts)}`}
          subColor={gapColor(comp.gapPts)}
        />
        <Stat value={fmtNum(num(row, "avg_pass_difficulty"), 2)} label="Avg difficulty" sub={posAvg(avg, "avg_pass_difficulty", 2)} />
        <Stat value={fmtNum(num(row, "psax_90"), 2)} label="PSAX /90" sub={posAvg(avg, "psax_90", 2)} />
        <Stat value={fmtNum(num(row, "key_passes_90"), 2)} label="Key passes /90" sub={posAvg(avg, "key_passes_90", 2)} />
        <Stat value={fmtNum(num(row, "minutes"), 0)} label="Minutes" />
      </div>
    </div>
  );
}

// ---- formatting (view-only; the maths lives in passing.ts) -----------------

function fmtNum(v: number | null, dp: number): string {
  return v === null ? "—" : v.toFixed(dp);
}
function fmtPct(v: number | null): string {
  return v === null ? "—" : `${v.toFixed(1)}%`;
}
/** Signed gap in points, e.g. "+2.4" / "−7.8". */
function fmtGap(v: number | null): string {
  if (v === null) return "—";
  const s = v.toFixed(1);
  return v > 0 ? `+${s}` : s.replace("-", "−");
}
function gapColor(v: number | null): string {
  if (v === null || v === 0) return theme.inkMuted;
  return v > 0 ? theme.accent : theme.danger;
}
/** "Positional avg 0.22" sub-label, or undefined when there's no peer group. */
function posAvg(avg: StatsRow | null, key: string, dp: number): string | undefined {
  const v = num(avg, key);
  return v === null ? undefined : `Positional avg ${v.toFixed(dp)}`;
}

// ---- small view pieces -----------------------------------------------------

function Stat({ value, label, sub, subColor }: { value: string; label: string; sub?: string; subColor?: string }) {
  return (
    <div style={{ background: theme.field, borderRadius: 8, padding: "10px 12px" }}>
      <div
        style={{
          color: theme.ink,
          fontSize: 20,
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        }}
      >
        {value}
      </div>
      <div style={{ color: theme.inkMuted, fontSize: 11, marginTop: 2 }}>{label}</div>
      {sub && <div style={{ color: subColor ?? theme.inkFaint, fontSize: 11, marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function Msg({ children, danger }: { children: React.ReactNode; danger?: boolean }) {
  return <div style={{ color: danger ? theme.danger : theme.inkMuted, fontSize: 13 }}>{children}</div>;
}
